import React, { useCallback, useContext, useEffect, useState } from 'react';

import { hot } from 'react-hot-loader/root';

import AJAX from 'common/AJAX';
import ExistingCompaniesForm from 'common/company/ExistingCompaniesForm';
import { LocationContext } from 'common/containers/RouterContainer';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Message from 'common/message/Message';
import SpinnerV2 from 'common/SpinnerV2';
import ButtonV2 from 'common/ui/ButtonV2';
import parseAPIResponse from 'common/util/parseAPIResponse';
import OktaLogo from 'img/landing/okta-logo.webp';

import 'css/components/okta/_OktaRedirect.scss';

type OktaCompany = {
  _id: string;
  name: string;
  subdomain: string;
};

type Props = {
  children: React.ReactNode;
  heading: string;
};

const ErrorMessages = {
  failedToConnect: 'Failed to connect to Okta. Please try again later.',
};

const FormErrors = {
  'company not found': ErrorMessages.failedToConnect,
  'okta not installed': ErrorMessages.failedToConnect,
  'not authorized': ErrorMessages.failedToConnect,
  'unable to fetch user data': ErrorMessages.failedToConnect,
  default: ErrorMessages.failedToConnect,
};

const OktaContainer = (props: Props) => {
  return (
    <div className="oktaRedirect">
      <LazyLoadedImage src={OktaLogo} alt="Okta logo" />
      <div className="heading">{props.heading}</div>
      {props.children}
    </div>
  );
};

const OktaRedirect = () => {
  const location = useContext(LocationContext);
  const [error, setError] = useState<string | null>(null);
  const [isRedirection, setIsRedirection] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [oktaCompanies, setOktaCompanies] = useState<OktaCompany[] | null>(null);
  const [selectedCompanyID, setSelectedCompanyID] = useState<string | null>(null);
  const defaultSelectedCompany = oktaCompanies?.[0];
  const selectedCompany =
    oktaCompanies?.find((company) => company._id === selectedCompanyID) ?? defaultSelectedCompany;

  const validateOkta = useCallback((validation: string) => {
    if (!window.opener) {
      throw new Error('missing window.opener');
    }

    const message = { validation };
    Message.postMessage(window.opener, '*', 'oktaValidation', message);
    window.close();
  }, []);

  const redirectTo = useCallback(async (companyID: string) => {
    setIsRedirecting(true);

    const responseJSON = await AJAX.post('/api/okta/connectUser', {
      companyID,
    });
    const { error, parsedResponse } = parseAPIResponse<{ redirectionURL: string }>(responseJSON, {
      isSuccessful: (parsedResponse) => !!parsedResponse.redirectionURL,
      errors: FormErrors,
    });

    if (error || !parsedResponse) {
      setError(error?.message ?? ErrorMessages.failedToConnect);
      setIsRedirecting(false);
      return;
    }

    const { redirectionURL } = parsedResponse;
    window.location.href = redirectionURL;
  }, []);

  const startOktaRedirection = useCallback(
    async (oktaAuthServerURL: string) => {
      setLoadingCompanies(true);

      const responseJSON = await AJAX.post('/api/okta/getCompanies', {
        oktaAuthServerURL,
      });

      const { error, parsedResponse } = parseAPIResponse<{ companies: OktaCompany[] }>(
        responseJSON,
        {
          isSuccessful: (parsedResponse) => !!parsedResponse.companies,
          errors: FormErrors,
        },
      );

      if (error || !parsedResponse) {
        setError(error?.message ?? ErrorMessages.failedToConnect);
        setLoadingCompanies(false);
        return;
      }

      const { companies } = parsedResponse;

      if (companies.length === 1) {
        await redirectTo(companies[0]._id);
        return;
      }

      setOktaCompanies(companies);
      setLoadingCompanies(false);
    },
    [redirectTo],
  );

  const onCompanyChange = (subdomain: string) => {
    const company = oktaCompanies?.find((company) => company.subdomain === subdomain);

    if (!company) {
      return;
    }

    setSelectedCompanyID(company._id);
  };

  const onRedirect = () => {
    if (selectedCompany) {
      redirectTo(selectedCompany._id);
    }
  };

  useEffect(() => {
    const { oktaAuthServerURL, validation } = location.query;

    if (validation) {
      validateOkta(validation);
      return;
    }

    if (oktaAuthServerURL) {
      setIsRedirection(true);
      startOktaRedirection(oktaAuthServerURL);
      return;
    }
  }, [location.query, validateOkta, startOktaRedirection]);

  if (!isRedirection) {
    return null;
  }

  if (error) {
    return (
      <OktaContainer heading="Failed to connect">
        <div className="error">{error}</div>
      </OktaContainer>
    );
  }

  if (loadingCompanies || !oktaCompanies) {
    return (
      <OktaContainer heading="Loading companies...">
        <SpinnerV2 className="spinner" size="large" />
      </OktaContainer>
    );
  }

  const haveCompanies = oktaCompanies.length > 0;
  const heading = haveCompanies ? 'Select Canny workspace' : 'No companies found';
  const validCompanyIDs = oktaCompanies.map((company) => company._id);

  return (
    <OktaContainer heading={heading}>
      {haveCompanies && (
        <div className="companySection">
          <div className="existingCompaniesFormSection">
            <ExistingCompaniesForm
              companies={oktaCompanies}
              fieldError={{ invalidOption: false }}
              selectedCompany={selectedCompany?.subdomain}
              setSelectedCompany={onCompanyChange}
              validCompanies={validCompanyIDs}
              options={{ showPlan: false }}
            />
          </div>
          <div className="buttonContainer">
            <ButtonV2
              className="initializeButton"
              color="primary"
              size="medium"
              loading={isRedirecting}
              onClick={onRedirect}>
              Connect
            </ButtonV2>
          </div>
        </div>
      )}
    </OktaContainer>
  );
};

export default hot(OktaRedirect);
